import { parseHex } from "./bytes";
import { encodeText, type TextEncoding } from "./encodings";

// Finding a run of bytes, in either direction, wrapping at whichever end it reaches. There is no index and no
// preprocessing: a plain scan over 32 MB is a few milliseconds, and an index would have to be rebuilt on every edit.

export type Mode = "hex" | "text";

export const MODE_OPTIONS = [{ value: "hex", label: "Hex" }, { value: "text", label: "Text" }];

export type Needle = { bytes: Uint8Array } | { error: string };

export function needleFor(query: string, mode: Mode, encoding: TextEncoding): Needle | null {
  if (query.trim() === "") return null;
  if (mode === "hex") {
    const bytes = parseHex(query);
    return bytes ? { bytes } : { error: "That is not a run of hex bytes — pairs of digits, spaced however you like." };
  }
  const encoded = encodeText(query, encoding);
  if ("missing" in encoded) return { error: `${encoding.label} has no byte for ${encoded.missing}.` };
  return { bytes: encoded };
}

// Both ends are inclusive of `from`, and stepping past a match is the caller's to say by asking from one byte on:
// the box being filled in searches from the cursor, and Next and Previous search from either side of it.
export function findNext(haystack: Uint8Array, needle: Uint8Array, from: number): number {
  const found = scan(haystack, needle, Math.max(0, from));
  // A second pass over the whole file rather than over the head of it: the first found nothing at or after `from`,
  // so the first match anywhere is the one that wraps to.
  return found >= 0 ? found : scan(haystack, needle, 0);
}

export function findPrevious(haystack: Uint8Array, needle: Uint8Array, from: number): number {
  const last = haystack.length - needle.length;
  const found = from < 0 ? -1 : scanBack(haystack, needle, Math.min(from, last));
  return found >= 0 ? found : scanBack(haystack, needle, last);
}

// Counted up to a ceiling and no further: what the number under the box is for is "some" against "one" against
// "none", and a file of zeros searched for a zero has more matches than anybody is going to step through.
export const MATCH_CAP = 1000;

export function countMatches(haystack: Uint8Array, needle: Uint8Array): number {
  let found = 0;
  let at = 0;
  while (found < MATCH_CAP) {
    const next = scan(haystack, needle, at);
    if (next < 0) break;
    found++;
    at = next + 1;
  }
  return found;
}

function scan(haystack: Uint8Array, needle: Uint8Array, from: number): number {
  const last = haystack.length - needle.length;
  const first = needle[0];
  for (let at = from; at <= last; at++) {
    // The first byte is checked before the call, which is the difference between a scan that is instant and one
    // that is not on a file where every other byte is a candidate.
    if (haystack[at] !== first) continue;
    if (equals(haystack, needle, at)) return at;
  }
  return -1;
}

function scanBack(haystack: Uint8Array, needle: Uint8Array, from: number): number {
  for (let at = from; at >= 0; at--) {
    if (haystack[at] !== needle[0]) continue;
    if (equals(haystack, needle, at)) return at;
  }
  return -1;
}

function equals(haystack: Uint8Array, needle: Uint8Array, at: number): boolean {
  for (let index = 1; index < needle.length; index++) {
    if (haystack[at + index] !== needle[index]) return false;
  }
  return true;
}

// An offset typed into the Go to box: `0x` or a bare run of hex digits, or a decimal number, held to the file.
export function parseOffset(text: string, size: number): number | { error: string } {
  const clean = text.trim().replace(/[\s,_]/g, "");
  if (clean === "") return { error: "Required" };
  const hex = /^(?:0x)?([0-9a-f]+)$/i.exec(clean);
  const decimal = /^([0-9]+)$/.exec(clean);
  // A run of nothing but digits is read as decimal, since that is what somebody typing a length or a size means;
  // anything with a letter in it, or anything behind `0x`, is the hex the offset column is spelled in.
  const value = decimal ? Number.parseInt(decimal[1], 10) : hex ? Number.parseInt(hex[1], 16) : Number.NaN;
  if (!Number.isFinite(value)) return { error: "That is not an offset — a decimal number, or hex behind `0x`." };
  if (size === 0) return { error: "This file has no bytes to go to." };
  if (value >= size) return { error: `This file ends at ${size - 1}.` };
  return value;
}
